import * as React from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AllValues,
  Create,
  fetchApi,
  FormManager,
  makeResetCache,
  notify,
  notifyResponse,
  PageTitle,
  useCloneEntity,
  useCacheRecord,
  UneditableReference,
} from 'gossamer-front-end';
import { CompoundResponse, getListRowLink } from 'gossamer-unified';
import {
  AdminCreateAttendanceRequest,
  Attendance,
  AttendanceRow,
  BookingExtendedView,
  BookingView,
  Event,
  UserView,
} from 'cb-model';
import { ControlAbsenceReasonField } from './ControlAbsenceReasonField';

export const AttendanceCreate = (): React.JSX.Element => {
  const { ticketBookingId } = useParams<{ ticketBookingId: string }>();
  const bookingRecord = useCacheRecord(BookingView, ticketBookingId);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const resetCache = makeResetCache(dispatch);

  const LocalAttendance = useCloneEntity(Attendance, (local) => {
    Object.values(local.fields).forEach((field) => {
      field.editable = false;
    });
    local.fields.attendance_result.editable = true;
    local.fields.absence_reason.editable = true;
    local.fields.user_comments.editable = true;
  });

  return (
    <>
      <PageTitle verb="Create" entity={Attendance} />
      {bookingRecord.data && (
        <div className="mb-3">
          for <UneditableReference refEntity={UserView} value={bookingRecord.data.user_id} />
          &nbsp; at <UneditableReference refEntity={Event} value={bookingRecord.data.event_id} />
        </div>
      )}
      <FormManager
        handleCancel={() => navigate(-1)}
        handleSubmit={async (values: AllValues) => {
          try {
            const attendanceRecord = values.main as unknown as AttendanceRow;
            const resp: CompoundResponse = await fetchApi('POST', '/main/admin-create-attendance', {
              ticketBookingId: attendanceRecord.ticket_booking_id,
              attendanceResult: attendanceRecord.attendance_result,
              absenceReason: attendanceRecord.absence_reason,
              comments: attendanceRecord.user_comments,
            } as AdminCreateAttendanceRequest);
            // reset cache
            resetCache(Attendance.id);
            resetCache(BookingView.id);
            resetCache(BookingExtendedView.id);
            notifyResponse(resp);
            navigate(-1);
          } catch (error) {
            const err = error as { statusCode?: number; message?: string };
            console.error(err.statusCode, err.message);
            notify([err.message || 'save failed', 'danger']);
          }
        }}
        handleSuccess={({ keyOfFirstCreate }) => navigate(getListRowLink(Attendance.listRowLink, keyOfFirstCreate))}
        initialization={{
          main: {
            type: 'create-single',
            entity: Attendance,
            override: {
              ticket_booking_id: ticketBookingId,
              recording_method: 'manual',
            },
          },
        }}
      >
        <ControlAbsenceReasonField localAttendance={LocalAttendance} />
        <Create valuesKey="main" entity={LocalAttendance} />
      </FormManager>
    </>
  );
};
